import React, { useEffect, useState, useCallback } from "react";
import { Bell, BellOff, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

const SUPPORTED =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY",
];

export default function NotifyAlertsButton() {
  const [open, setOpen] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stateFilter, setStateFilter] = useState("");

  // Register SW once per session and probe existing subscription.
  useEffect(() => {
    if (!SUPPORTED) return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.register("/sw.js");
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setSubscribed(!!sub);
      } catch (e) {
        // Silent — user can still see the button and try.
        console.warn("SW register failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const subscribe = useCallback(async () => {
    if (!SUPPORTED) {
      toast.error("Your browser doesn't support push notifications.");
      return;
    }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Notification permission denied.");
        setBusy(false);
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
      await api.post("/push/subscribe", {
        subscription: sub.toJSON(),
        state: stateFilter || null,
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      toast.success(
        stateFilter
          ? `Subscribed to ${stateFilter} alerts.`
          : "Subscribed to all-state alerts."
      );
      setOpen(false);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't enable alerts. Try again.");
    } finally {
      setBusy(false);
    }
  }, [stateFilter]);

  const unsubscribe = useCallback(async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Alerts turned off.");
      setOpen(false);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't unsubscribe.");
    } finally {
      setBusy(false);
    }
  }, []);

  if (!SUPPORTED) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-2 border px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${
          subscribed
            ? "bg-green-50 border-green-700 text-green-800 hover:bg-green-100"
            : "border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white"
        }`}
        data-testid="notify-alerts-btn"
        title={subscribed ? "You're subscribed to alerts" : "Get notified of new convictions"}
      >
        {subscribed ? <Bell size={13} /> : <Bell size={13} />}
        {subscribed ? "Alerts on" : "Get alerts"}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="notify-modal"
        >
          <div
            className="bg-white border-2 border-zinc-900 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between border-b border-red-600">
              <div>
                <div className="label-eyebrow text-red-500">Push · Alerts</div>
                <div className="font-display font-black text-xl mt-1">
                  {subscribed ? "Alerts are on" : "Get conviction alerts"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => !busy && setOpen(false)}
                className="text-white hover:text-red-500"
                aria-label="Close"
                data-testid="notify-modal-close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-5 text-sm text-zinc-800 leading-relaxed space-y-4">
              {subscribed ? (
                <>
                  <p>
                    You'll receive a push notification when a new convicted
                    abuser is added to the registry. Notifications work even
                    when this site isn't open.
                  </p>
                  <button
                    type="button"
                    onClick={unsubscribe}
                    disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 border border-red-600 text-red-700 hover:bg-red-600 hover:text-white px-4 py-3 text-xs font-bold uppercase tracking-widest"
                    data-testid="notify-unsubscribe"
                  >
                    <BellOff size={14} />
                    {busy ? "Turning off…" : "Turn off alerts"}
                  </button>
                </>
              ) : (
                <>
                  <p>
                    Get a push notification each time a new convicted abuser is
                    added to Paws on Record. Useful for shelters and rescues
                    screening adopters.
                  </p>

                  <div>
                    <label
                      htmlFor="notify-state"
                      className="label-eyebrow block mb-2"
                    >
                      State (optional)
                    </label>
                    <select
                      id="notify-state"
                      value={stateFilter}
                      onChange={(e) => setStateFilter(e.target.value)}
                      className="w-full border border-zinc-900 px-3 py-2 text-sm bg-white"
                      data-testid="notify-state-select"
                    >
                      <option value="">All 50 states (default)</option>
                      {US_STATES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <p className="text-[11px] text-zinc-500 mt-2">
                      Pick a state to only receive alerts for new records there.
                    </p>
                  </div>

                  <div className="flex items-start gap-2 text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 p-3">
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                    <span>
                      Your browser will ask for permission. We never collect
                      your email or contact info — subscriptions are anonymous.
                      Unsubscribe anytime.
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={subscribe}
                    disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 bg-zinc-900 text-white border border-zinc-900 hover:bg-red-600 hover:border-red-600 px-4 py-3 text-xs font-bold uppercase tracking-widest"
                    data-testid="notify-subscribe"
                  >
                    <Bell size={14} />
                    {busy ? "Subscribing…" : "Enable alerts"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
